import type { ReactNode } from "react";
import { LocalizedLink as Link } from "@/components/routing/LocalizedLink";
import { ArrowLeft } from "lucide-react";
import { useTranslation } from "react-i18next";
import { LanguageSwitcher } from "@/components/controls/LanguageSwitcher";
import { ThemeToggle } from "@/components/controls/ThemeToggle";
import { BrandLogo } from "@/components/BrandLogo";
import authMural from "@/assets/hero-auth.jpg";

interface Props {
  children: ReactNode;
  side?: ReactNode;
}

/**
 * Split-panel layout used by /login and /signup. Right-side panel is hidden on
 * mobile so the form is centered. Top bar contains brand + back link + language
 * and theme controls.
 */
export function AuthSplitLayout({ children, side }: Props) {
  const { t } = useTranslation();
  return (
    <div className="flex min-h-screen w-full bg-surface-1 text-text-1">
      <div className="flex w-full flex-col px-6 py-6 md:w-1/2 md:px-12">
        <div className="flex items-center justify-between">
          <BrandLogo />
          <div className="flex items-center gap-1">
            <LanguageSwitcher />
            <ThemeToggle />
            <Link
              to="/"
              className="ml-2 hidden items-center gap-1 t-body-sm text-text-3 transition hover:text-text-1 sm:inline-flex"
            >
              <ArrowLeft className="h-3.5 w-3.5" />
              {t("auth.backToSite")}
            </Link>
          </div>
        </div>
        <div className="flex flex-1 items-center">
          <div className="mx-auto w-full max-w-md py-10">{children}</div>
        </div>
      </div>
      {side ? (
        <aside className="relative hidden w-1/2 overflow-hidden border-l border-border-1 md:flex">
          <img
            src={authMural}
            alt=""
            aria-hidden
            className="absolute inset-0 h-full w-full object-cover"
            loading="lazy"
            decoding="async"
          />
          <div
            aria-hidden
            className="absolute inset-0"
            style={{
              background:
                "linear-gradient(135deg, hsl(var(--primary) / 0.55) 0%, hsl(var(--primary) / 0.35) 50%, hsl(var(--primary) / 0.65) 100%)",
            }}
          />
          <div className="relative m-auto max-w-md p-12 text-white">{side}</div>
        </aside>
      ) : null}
    </div>
  );
}
